import { z } from 'zod';
import { Array as A, Option, pipe } from 'effect';

import { isServiceColumn } from '@/components/AgGrid/helpers';

// What the workspace applies from a saved column: sorting is saved apart (`gridSorting`), and the other keys of
// AG Grid's column state change between versions (36 added five), so keeping them only makes layouts unequal.
const SavedColumnSchema = z.object({
  colId: z.string(),
  hide: z
    .boolean()
    .nullish()
    .transform((hide) => hide ?? false),
  pinned: z
    .union([z.enum(['left', 'right']), z.boolean()])
    .nullish()
    .transform((pinned) => (pinned === true ? 'left' : pinned || null)),
  width: z
    .number()
    .nullish()
    .transform((width) => width ?? undefined),
  flex: z
    .number()
    .nullish()
    .transform((flex) => flex ?? null),
});

export type SavedColumn = z.output<typeof SavedColumnSchema>;
export type SavedColumnLayout = ReadonlyArray<SavedColumn>;

// A workspace layout lists data columns only. Technical columns are fixed by their column definitions, and AG Grid
// puts them back at their locked edge, so leaving them out keeps a layout valid when code changes them (AG Grid 36
// narrowed the master/detail expander). Presets and drafts come from the API and localStorage untyped; a column
// that does not parse is dropped.
export const toSavedColumnLayout = (columns: ReadonlyArray<unknown>): SavedColumnLayout =>
  pipe(
    columns,
    A.filterMap((column) => Option.fromNullable(SavedColumnSchema.safeParse(column).data)),
    A.filter(({ colId }) => !isServiceColumn(colId)),
  );

// The same rule as a schema, for a layout parsed as a whole (the workspace's stored layout).
export const SavedColumnLayoutSchema = z.array(z.unknown()).transform(toSavedColumnLayout);

export const getHiddenColIds = (columns: ReadonlyArray<unknown>): ReadonlyArray<string> =>
  pipe(
    toSavedColumnLayout(columns),
    A.filter(({ hide }) => hide),
    A.map(({ colId }) => colId),
  );
